import React, { useRef } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { LOGOS } from "../../lib/defaultFlyer";
import { Upload, RotateCcw } from "lucide-react";

const Field = ({ label, children, testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    <Label className="text-xs uppercase tracking-wider text-stone-500">
      {label}
    </Label>
    {children}
  </div>
);

const FlyerForm = ({ flyer, setFlyer, onReset }) => {
  const fileRef = useRef(null);

  const set = (path, value) => {
    setFlyer((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setArrayItem = (path, index, key, value) => {
    setFlyer((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (const k of keys) cur = cur[k];
      cur[index][key] = value;
      return next;
    });
  };

  const setListItem = (path, index, value) => {
    setFlyer((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (const k of keys) cur = cur[k];
      cur[index] = value;
      return next;
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("customLogo", reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2
          className="text-lg font-bold text-[#114232]"
          style={{ fontFamily: "Chewy, system-ui" }}
          data-testid="form-title"
        >
          Customise your flyer
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          data-testid="reset-flyer-btn"
          className="text-stone-500 hover:text-[#114232]"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
        </Button>
      </div>

      <Accordion
        type="multiple"
        defaultValue={["brand", "hero", "benefits"]}
        className="space-y-2"
      >
        {/* ---------- Brand / Logo ---------- */}
        <AccordionItem
          value="brand"
          className="border border-stone-200 rounded-xl px-3 bg-white"
        >
          <AccordionTrigger
            className="text-sm font-semibold text-[#114232]"
            data-testid="brand-section-toggle"
          >
            Brand & Logo
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <Field label="Brand name" testId="field-brand">
              <Input
                value={flyer.brand}
                onChange={(e) => set("brand", e.target.value)}
                data-testid="input-brand"
              />
            </Field>
            <Field label="Tagline" testId="field-tagline">
              <Input
                value={flyer.tagline}
                onChange={(e) => set("tagline", e.target.value)}
                data-testid="input-tagline"
              />
            </Field>
            <Field label="Logo variant" testId="field-logo-variant">
              <div className="grid grid-cols-3 gap-2">
                {LOGOS.map((l) => {
                  const active = flyer.logoId === l.id && !flyer.customLogo;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      data-testid={`logo-variant-${l.id}`}
                      onClick={() => {
                        set("customLogo", null);
                        set("logoId", l.id);
                      }}
                      className={`rounded-xl p-2 border transition-all ${
                        active
                          ? "border-[#114232] ring-2 ring-[#114232]/20"
                          : "border-stone-200 hover:border-[#94A596]"
                      }`}
                    >
                      <img
                        src={l.src}
                        alt={l.label}
                        className="w-full h-14 object-contain"
                      />
                      <p className="text-[10px] mt-1 text-stone-500 text-center">
                        {l.label}
                      </p>
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Upload your own logo (PNG/JPG)" testId="field-custom-logo">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  data-testid="upload-logo-btn"
                  className="border-[#94A596] text-[#114232]"
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload
                </Button>
                {flyer.customLogo && (
                  <>
                    <img
                      src={flyer.customLogo}
                      alt="custom"
                      className="w-10 h-10 object-contain rounded border border-stone-200"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => set("customLogo", null)}
                      data-testid="remove-custom-logo-btn"
                      className="text-stone-500"
                    >
                      Remove
                    </Button>
                  </>
                )}
              </div>
            </Field>
          </AccordionContent>
        </AccordionItem>

        {/* ---------- Hero ---------- */}
        <AccordionItem
          value="hero"
          className="border border-stone-200 rounded-xl px-3 bg-white"
        >
          <AccordionTrigger
            className="text-sm font-semibold text-[#114232]"
            data-testid="hero-section-toggle"
          >
            Headline
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <Field label="Eyebrow">
              <Input
                value={flyer.eyebrow}
                onChange={(e) => set("eyebrow", e.target.value)}
                data-testid="input-eyebrow"
              />
            </Field>
            <Field label="Headline">
              <Textarea
                rows={2}
                value={flyer.headline}
                onChange={(e) => set("headline", e.target.value)}
                data-testid="input-headline"
              />
            </Field>
            <Field label="Sub-headline">
              <Textarea
                rows={3}
                value={flyer.subheadline}
                onChange={(e) => set("subheadline", e.target.value)}
                data-testid="input-subheadline"
              />
            </Field>
          </AccordionContent>
        </AccordionItem>

        {/* ---------- Benefits ---------- */}
        <AccordionItem
          value="benefits"
          className="border border-stone-200 rounded-xl px-3 bg-white"
        >
          <AccordionTrigger
            className="text-sm font-semibold text-[#114232]"
            data-testid="benefits-section-toggle"
          >
            Key benefits (3 cards)
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            {flyer.benefits.map((b, i) => (
              <div
                key={i}
                className="space-y-2 p-3 rounded-lg bg-stone-50 border border-stone-200"
              >
                <Field label={`Card ${i + 1} title`}>
                  <Input
                    value={b.title}
                    onChange={(e) =>
                      setArrayItem("benefits", i, "title", e.target.value)
                    }
                    data-testid={`input-benefit-title-${i}`}
                  />
                </Field>
                <Field label="Description">
                  <Textarea
                    rows={2}
                    value={b.body}
                    onChange={(e) =>
                      setArrayItem("benefits", i, "body", e.target.value)
                    }
                    data-testid={`input-benefit-body-${i}`}
                  />
                </Field>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* ---------- Stats ---------- */}
        <AccordionItem
          value="stats"
          className="border border-stone-200 rounded-xl px-3 bg-white"
        >
          <AccordionTrigger
            className="text-sm font-semibold text-[#114232]"
            data-testid="stats-section-toggle"
          >
            Stat strip
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            {flyer.stats.map((st, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <Field label={`Stat ${i + 1} value`}>
                  <Input
                    value={st.value}
                    onChange={(e) =>
                      setArrayItem("stats", i, "value", e.target.value)
                    }
                    data-testid={`input-stat-value-${i}`}
                  />
                </Field>
                <Field label="Label">
                  <Input
                    value={st.label}
                    onChange={(e) =>
                      setArrayItem("stats", i, "label", e.target.value)
                    }
                    data-testid={`input-stat-label-${i}`}
                  />
                </Field>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* ---------- Process ---------- */}
        <AccordionItem
          value="process"
          className="border border-stone-200 rounded-xl px-3 bg-white"
        >
          <AccordionTrigger
            className="text-sm font-semibold text-[#114232]"
            data-testid="process-section-toggle"
          >
            How it works (3 steps)
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            {flyer.process.map((p, i) => (
              <div
                key={i}
                className="space-y-2 p-3 rounded-lg bg-stone-50 border border-stone-200"
              >
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Step">
                    <Input
                      value={p.step}
                      onChange={(e) =>
                        setArrayItem("process", i, "step", e.target.value)
                      }
                      data-testid={`input-process-step-${i}`}
                    />
                  </Field>
                  <div className="col-span-2">
                    <Field label="Title">
                      <Input
                        value={p.title}
                        onChange={(e) =>
                          setArrayItem("process", i, "title", e.target.value)
                        }
                        data-testid={`input-process-title-${i}`}
                      />
                    </Field>
                  </div>
                </div>
                <Field label="Description">
                  <Textarea
                    rows={2}
                    value={p.body}
                    onChange={(e) =>
                      setArrayItem("process", i, "body", e.target.value)
                    }
                    data-testid={`input-process-body-${i}`}
                  />
                </Field>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* ---------- Requirements ---------- */}
        <AccordionItem
          value="requirements"
          className="border border-stone-200 rounded-xl px-3 bg-white"
        >
          <AccordionTrigger
            className="text-sm font-semibold text-[#114232]"
            data-testid="requirements-section-toggle"
          >
            The deal (You provide / We provide)
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">
                You provide
              </Label>
              {flyer.requirements.youProvide.map((it, i) => (
                <Input
                  key={i}
                  value={it}
                  onChange={(e) =>
                    setListItem(
                      "requirements.youProvide",
                      i,
                      e.target.value
                    )
                  }
                  data-testid={`input-you-provide-${i}`}
                  className="mt-1.5"
                />
              ))}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">
                We provide
              </Label>
              {flyer.requirements.weProvide.map((it, i) => (
                <Input
                  key={i}
                  value={it}
                  onChange={(e) =>
                    setListItem(
                      "requirements.weProvide",
                      i,
                      e.target.value
                    )
                  }
                  data-testid={`input-we-provide-${i}`}
                  className="mt-1.5"
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ---------- Contact ---------- */}
        <AccordionItem
          value="contact"
          className="border border-stone-200 rounded-xl px-3 bg-white"
        >
          <AccordionTrigger
            className="text-sm font-semibold text-[#114232]"
            data-testid="contact-section-toggle"
          >
            Call to action & contact
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <Field label="CTA line">
              <Textarea
                rows={2}
                value={flyer.cta}
                onChange={(e) => set("cta", e.target.value)}
                data-testid="input-cta"
              />
            </Field>
            <Field label="Contact name">
              <Input
                value={flyer.contact.name}
                onChange={(e) => set("contact.name", e.target.value)}
                data-testid="input-contact-name"
              />
            </Field>
            <Field label="Role">
              <Input
                value={flyer.contact.role}
                onChange={(e) => set("contact.role", e.target.value)}
                data-testid="input-contact-role"
              />
            </Field>
            <Field label="Email">
              <Input
                value={flyer.contact.email}
                onChange={(e) => set("contact.email", e.target.value)}
                data-testid="input-contact-email"
              />
            </Field>
            <Field label="Phone">
              <Input
                value={flyer.contact.phone}
                onChange={(e) => set("contact.phone", e.target.value)}
                data-testid="input-contact-phone"
              />
            </Field>
            <Field label="Website">
              <Input
                value={flyer.contact.website}
                onChange={(e) => set("contact.website", e.target.value)}
                data-testid="input-contact-website"
              />
            </Field>
            <Field label="Location">
              <Input
                value={flyer.contact.location}
                onChange={(e) => set("contact.location", e.target.value)}
                data-testid="input-contact-location"
              />
            </Field>
            <Field label="Footer note">
              <Input
                value={flyer.footerNote}
                onChange={(e) => set("footerNote", e.target.value)}
                data-testid="input-footer-note"
              />
            </Field>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default FlyerForm;
